import Pristine from 'pristinejs';
import { Cookie } from 'storage-manager-js';
import jump from 'jump.js';
import dayjs from 'dayjs';
import { setupStripeElements } from './donation-stripe';
import { initialisePayframe } from './donation-mw';

Alpine.data('donation', (options) => {
	let gatewayInstance;
	let gatewayInstanceParams;
	let autocomplete;
	const occasions = options.occasions;
	const pristineConfig = {
		classTo: 'gfield',
		errorClass: 'gfield_error',
		successClass: 'gfield_success',
		errorTextParent: 'gfield',
		errorTextTag: 'div',
		errorTextClass: 'gfield_validation_message',
	};

	const paypalCookieName = 'aa-donation-first-name';
	const paymentGatewayError =
		'Could not initialise payment gateway. Please refresh the page and try again.';

	const isDefault = options.inputDefaults.formVariant === '';
	const isUpdate = options.inputDefaults.formVariant === 'update';
	const updateType = options.inputDefaults.formSubVariant;
	const isMembershipRenewal =
		options.inputDefaults.formVariant === 'membership-renewal';
	const isMembershipNew =
		options.inputDefaults.formVariant === 'membership-new';
	const isAppeal = options.isAppeal || false;

	const endpoint = 'donations/payment';

	const formTrackingDetails = {
		interactionStartTime: null,
		isDirty: false,
		fieldHistory: [],
		isNotSubmit: true,
	};

	const defaultDonationType = options.inputDefaults.donationFrequency;

	return {
		currentStep: 0,
		paymentSuccessful: null,
		paypalError: options.paypalError || '',
		pdfDownloadUrl: '',
		error: null,
		submitStatus: null,
		loading: false,
		processing: false,
		isUpdate,
		isMembership: options.isMembership,
		forceFrequency: options.forceFrequency,
		addressManualInput: false,
		minDonationAmount: 0,
		isMemoryOf: false,
		donationType: defaultDonationType,
		donationAmountByType: {},
		inputs: {
			isGift: false,
			gateway: options.gateway,
			url: window.location.href,
			paymentOption: 'cc',
			country: '',
			inEU: null,
			giftOccasion: null,
			giftRecipientName: null,
			giftMessage: null,
		},

		init() {
			setTimeout(() => {
				this.$store.aa.observeFormInteraction(
					this.$refs.step0form,
					formTrackingDetails
				);
				this.$store.aa.observeFormInteraction(
					this.$refs.step1form,
					formTrackingDetails
				);
				this.$store.aa.observeFormInteraction(
					this.$refs.step2form,
					formTrackingDetails
				);
				this.$store.aa.trackFormAbandonment(formTrackingDetails, {
					form_name: this.isMembership ? 'member signup' : 'donate',
					form_variant: 'standard',
				});
			}, 1000);

			if (!isUpdate) {
				// Listen to when the Google Places Autocomplete script is ready for us to use.
				const interval = setInterval(() => {
					if (typeof google?.maps?.places?.Autocomplete === 'function') {
						clearInterval(interval);

						this.initAddressPlacesAutocomplete();
					}
				}, 500);
				document.addEventListener('formStepper:change', () => {
					this.currentStep = this.$store.aa.formStepper.current;
				});

				// Whenever the donation frequency changes, reset the payment option back to credit card.
				this.$watch('inputs.donationFrequency', () => {
					if (this.inputs.donationFrequency === 'monthly') {
						this.inputs.isGift = false;
					}

					this.inputs.paymentOption = 'cc';
					this.calcMinDonationAmount();
				});
			}

			this.$watch('inputs.paymentOption', () => {
				if (
					this.inputs.paymentOption === 'cc' &&
					gatewayInstance &&
					options.gateway === 'mw'
				) {
					// MW payframe wouldn't display all the payment fields if the container was hidden when doing initialisation.
					gatewayInstance.reset();
				}
			});

			return () => {
				this.inputs.inEU = this.$store.aa.userInEU;

				this.$watch('inputs.country', (value, oldValue) => {
					if (value !== oldValue) {
						this.$store.aa.isEU(this.inputs.country).json((response) => {
							this.inputs.inEU = response.data;
						});
					}
				});

				this.$watch('donationAmountByType', () => {
					this.inputs.donationAmount = this.getDonationAmount();
				});

				this.$watch('inputs.donationAmountOther', () => {
					this.inputs.donationAmount = this.getDonationAmount();
				});

				// Override defaults based on what's externally provided.
				if (options.inputDefaults) {
					this.inputs = {
						...this.inputs,
						...options.inputDefaults,
					};
				}

				this.calcMinDonationAmount();

				// If the URL contains `?payment-completed`, we'll skip to the last step.
				if (window.location.search.includes('payment-completed')) {
					this.inputs.firstName = Cookie.get(paypalCookieName);

					this.setGiftCertificatePDFURL(Cookie.get('_aa_gift_url'));

					this.goToConfirmation(!this.paypalError);
				}
				// If the URL contains `?payment-failed`, we'll skip to the last step.
				if (window.location.search.includes('payment-failed')) {
					this.goToConfirmation(false);
				}

				// On the update CC form, we'll skip the first 2 steps and prepare the payment gateway.
				if (isUpdate) {
					this.preparePaymentGateway();

					this.currentStep = 2;
				}

				this.$nextTick(() => {
					this.preselectDollarHandles();
				});
			};
		},

		preselectDollarHandles() {
			const oneOff = options.preselectDonationFigures.oneOff;
			const monthly = options.preselectDonationFigures.monthly;

			if (this.inputs.donationFrequency === 'one-off') {
				this.donationAmountByType['one-off'] =
					options.preselectDonationFigures.oneOffValues[oneOff];
			} else {
				this.donationAmountByType.monthly =
					options.preselectDonationFigures.monthlyValues[monthly];
			}
		},

		initMerchantWarrior() {
			const isRecurringPayment = !!(
				isUpdate || this.inputs.donationFrequency === 'monthly'
			);

			gatewayInstance = initialisePayframe(
				options.uuid,
				options.apiKey,
				options.apiBase,
				options.apiBaseUrl,
				isRecurringPayment
			);

			const sendPaymentRequest = (data) => {
				this.inputs.donationAmount = this.getDonationAmount();

				this.submitCreditCardPaymentRequest({
					...data,
					...this.inputs,
				});
			};

			gatewayInstance.mwCallback = () => {
				if (
					gatewayInstance.responseCode === -2 &&
					gatewayInstance.responseMessage === 'Invalid message type'
				) {
					// Internal MW error (e.g. "Invalid message type"); do nothing
					console.log('ignoring unhandled MW error');
					return;
				}

				this.loading = false;

				if (isRecurringPayment) {
					sendPaymentRequest({
						cardId: gatewayInstance.cardID,
					});
				} else if (gatewayInstance.tokenStatus === 'HAS_TOKEN') {
					sendPaymentRequest({
						payframeToken: gatewayInstance.payframeToken,
						payframeKey: gatewayInstance.payframeKey,
					});
				} else {
					this.error = paymentGatewayError;

					this.$store.aa.captureSentryError(this.error, gatewayInstance);
				}

				// Reset - allowing for another entry.
				// Unfortunately, this clears out the forms :(
				gatewayInstance.reset();
			};

			gatewayInstance.loading = () => {
				this.loading = true;
			};
			gatewayInstance.loaded = () => {
				this.loading = false;
			};

			gatewayInstance.deploy();
		},
		getStripePaymentIntent() {
			gatewayInstance = Stripe(options.apiKey);

			// Get the amount, convert to cents (lowest unit).
			const amount = this.getDonationAmount() * 100;

			this.loading = true;

			this.$store.aa
				.api('donations/stripe')
				.post({
					amount,
				})
				.json((response) => {
					this.loading = false;

					const { clientSecret } = response;

					// Save the token with the inputs to pass to the payment endpoint.
					this.inputs.stripeToken = clientSecret;

					gatewayInstanceParams = setupStripeElements(
						gatewayInstance,
						clientSecret
					);
				});
		},
		initPayPal() {
			this.processing = true;

			this.$store.aa
				.api(endpoint)
				.post({
					...this.inputs,
				})
				.json((response) => {
					if (response.success) {
						const actions = response?.data?.actions;

						let redirect_url = '';

						actions?.length &&
							actions.forEach((action) => {
								if (action.type === 'paypal.create') {
									redirect_url = action?.result?.approve_url;
								}
							});

						if (redirect_url) {
							Cookie.set(paypalCookieName, this.inputs.firstName, {
								expires: 1, // 1 day.
							});

							setTimeout(() => {
								window.location.href = redirect_url;
							}, 50);
						} else {
							this.processing = false;
							this.error = paymentGatewayError;

							this.$store.aa.captureSentryError(this.error, response);
						}
					} else {
						this.processing = false;
						this.error = response.message || null;

						this.$store.aa.captureSentryError(this.error, response);
					}

					// this.goToConfirmation(true);

					// Track event.
					this.trackEvent();
				})
				.catch((response) => {
					this.processing = false;
					this.error =
						response?.json?.message ||
						response?.message ||
						'Something went wrong. Please contact Animals Australia.';

					this.$store.aa.captureSentryError(this.error, response);
				});
		},

		submitDebitCardPaymentRequest(postData = {}) {
			this.error = null;
			this.processing = true;

			this.$store.aa
				.api(endpoint)
				.post(postData)
				.json((response) => {
					this.processing = false;

					if (response.success) {
						this.goToConfirmation(true);

						this.trackEvent(response.data);
					} else {
						this.error = response.message || null;

						this.$store.aa.captureSentryError(this.error, response);
					}
				})
				.catch((response) => {
					this.processing = false;
					this.error =
						response?.json?.message ||
						response?.message ||
						'Something went wrong. Please contact Animals Australia.';

					this.$store.aa.captureSentryError(this.error, response);
				});
		},
		submitCreditCardPaymentRequest(postData = {}) {
			this.error = null;
			this.processing = true;

			this.$store.aa
				.api(endpoint)
				.post(postData)
				.json((response) => {
					this.processing = false;

					if (response.success) {
						this.goToConfirmation(true);

						this.trackEvent(response.data);

						this.setGiftCertificatePDFURL(response.gift_certificate);
					} else {
						this.error = response.message || null;

						this.$store.aa.captureSentryError(this.error, response);
					}
				})
				.catch((response) => {
					this.processing = false;
					this.error =
						response?.json?.message ||
						response?.message ||
						'Something went wrong. Please contact Animals Australia.';

					this.$store.aa.captureSentryError(this.error, response);
				});
		},

		submitStripePayment() {
			formTrackingDetails.isNotSubmit = false;

			if (this.inputs.donationFrequency === 'one-off') {
				gatewayInstance
					.createToken(gatewayInstanceParams.cardNumber)
					.then((response) => {
						if (response.error && response.error.message) {
							this.error = response.error.message;

							this.$store.aa.captureSentryError(this.error, response);
						} else if (response.token && response.token.id) {
							this.submitCreditCardPaymentRequest({
								...this.inputs,
								stripeToken: response.token.id,
							});
						}
					});
			} else {
				gatewayInstance
					.createSource(gatewayInstanceParams.cardNumber)
					.then((response) => {
						if (response.error && response.error.message) {
							this.error = response.error.message;

							this.$store.aa.captureSentryError(this.error, response);
						} else if (response.source && response.source.id) {
							this.submitCreditCardPaymentRequest({
								...this.inputs,
								stripeToken: response.source.id,
							});
						}
					});
			}
		},

		initAddressPlacesAutocomplete() {
			// Don't submit the form when users press enter in the google address field
			this.$refs.addressAutocomplete.addEventListener('keydown', (event) => {
				if (event.keyCode === 13) {
					event.preventDefault();
				}
			});

			let componentRestrictions = null;
			if (options.isAustralia) {
				// If set to AU, only allow searching within that country.
				componentRestrictions = {
					country: 'AU',
				};
			}

			autocomplete = new google.maps.places.Autocomplete(
				this.$refs.addressAutocomplete,
				{
					/*
				https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service?hl=zh-cn#AutocompletionRequest.componentRestrictions
				Autocomplete results will be biased towards, but not restricted to, the given bounds.
				 */
					componentRestrictions,
					fields: ['address_components', 'geometry'],
				}
			);

			const findComponent = (components, type, name = 'long_name') => {
				if (!components?.length) return '';

				const component = components.find((c) => c.types.includes(type));

				return (component && component[name]) || '';
			};

			// Make sure the places autocomplete field is registered with Alpine.
			google.maps.event.addListener(autocomplete, 'place_changed', () => {
				// Reset the address fields.
				this.inputs.address = '';
				this.inputs.suburb = '';
				this.inputs.state = '';
				this.inputs.postcode = '';
				this.inputs.country = '';

				const place = autocomplete.getPlace();

				let subPremise = findComponent(
					place.address_components,
					'subpremise',
					'long_name'
				);
				const streetNumber = findComponent(
					place.address_components,
					'street_number',
					'long_name'
				);
				const streetName = findComponent(
					place.address_components,
					'route',
					'long_name'
				);

				subPremise = subPremise ? `${subPremise}/` : '';

				this.inputs.address =
					`${subPremise}${streetNumber} ${streetName}`.trim();
				this.inputs.suburb = findComponent(
					place.address_components,
					'locality',
					'long_name'
				);
				this.inputs.postcode = findComponent(
					place.address_components,
					'postal_code',
					'long_name'
				);
				this.inputs.country = findComponent(
					place.address_components,
					'country',
					'short_name'
				);

				// State should be the short name for AU, long name for anything else.
				this.inputs.state = findComponent(
					place.address_components,
					'administrative_area_level_1',
					this.inputs.country === 'AU' ? 'short_name' : 'long_name'
				);

				this.$nextTick(() => {
					this.addressManualInput = true;

					this.handleStep1Submit(true);
				});
			});
		},
		hasValidAddress() {
			const parts = ['address', 'suburb', 'state', 'postcode', 'country'];

			return parts.every((part) => !!this.inputs[part]);
		},

		nextStep() {
			this.currentStep += 1;
			this.$store.aa.formStepper.next();

			this.scrollToTopOfForm();
		},
		previousStep() {
			this.currentStep = Math.max(0, this.currentStep - 1);
			this.$store.aa.formStepper.prev();

			if (isAppeal && this.currentStep === 0) {
				this.$dispatch('appealspotlightfocus');
			} else {
				this.scrollToTopOfForm();
			}
		},
		goToConfirmation(paymentSuccessful) {
			this.paymentSuccessful = paymentSuccessful;
			this.currentStep = 3;

			this.scrollToTopOfForm();

			// Trigger the Lottie event to make the lottie animation play when we're at the success state.
			setTimeout(() => {
				document.dispatchEvent(new Event('lottie.donation-success.play'));
			}, 100);
		},
		scrollToTopOfForm() {
			jump(this.$refs.top, {
				duration: 300,
				offset: -100,
				a11y: true,
			});
		},
		scrollToFirstErrorMessage(pristineInstance) {
			const fistInvalidControl = pristineInstance.getErrors()[0];

			fistInvalidControl &&
				fistInvalidControl.input &&
				jump(fistInvalidControl.input, {
					duration: 300,
					offset: -200,
					a11y: true,
				});
		},
		focusOtherDonationAmount(frequency) {
			setTimeout(() => {
				// Must use a query selector since we're using `x-if` to show/hide elements.
				// This prevents us from using `x-ref`.
				this.$root
					.querySelector(`#donation-amount-other-input-${frequency}`)
					.focus();
			}, 100);
		},
		focusOtherAddAmount(frequency) {
			setTimeout(() => {
				// Must use a query selector since we're using `x-if` to show/hide elements.
				// This prevents us from using `x-ref`.
				this.$root
					.querySelector(`#donation-add-other-input-${frequency}`)
					.focus();
			}, 100);
		},
		getDonationAmount() {
			const donationValue = this.donationAmountByType[this.donationType] ?? 0;

			if (donationValue === 'other') {
				return this.inputs.donationAmountOther;
			}

			return donationValue;
		},

		// clear the selected value when change between one-off and monthly.
		changeDonationType(type) {
			const sameType = this.donationType === type;

			this.donationType = type;
			this.inputs.donationAmountOther = '';

			Object.keys(this.donationAmountByType).forEach((key) => {
				if (this.donationType !== key) {
					delete this.donationAmountByType[key];
				}
			});

			// Preselect the dollar handles for the new donation type, only if the type has changed.
			if (!sameType) {
				this.$nextTick(() => {
					this.preselectDollarHandles();
				});
			}
		},

		// switch between one-off and monthly in standalone donation
		switchDonationType() {
			if (this.inputs.donationFrequency === 'one-off') {
				this.inputs.donationFrequency = 'monthly';
			} else if (this.inputs.donationFrequency === 'monthly') {
				this.inputs.donationFrequency = 'one-off';
			}
		},
		calcMinDonationAmount() {
			if (this.isMembership) {
				this.minDonationAmount = 35;
			} else if (isUpdate) {
				this.minDonationAmount = 1;
			} else {
				this.minDonationAmount = 5;
			}
		},
		formatCurrency(amount) {
			return new Intl.NumberFormat('en-AU', {
				style: 'currency',
				currency: 'AUD',
			}).format(amount);
		},
		getIncrementedDonationAmount(currentAmount) {
			let addAmount = this.inputs.addAmount;

			if (addAmount === 'other') {
				addAmount = this.inputs.addAmountOther;
			}

			const newTotal = parseFloat(addAmount) + parseFloat(currentAmount);

			if (Number.isNaN(newTotal)) return this.formatCurrency(currentAmount);

			return this.formatCurrency(newTotal);
		},
		getAddAmount() {
			let addAmount = this.inputs.addAmount;

			if (addAmount === 'other') {
				addAmount = this.inputs.addAmountOther;
			}

			return addAmount;
		},

		preparePaymentGateway() {
			let interval;

			if (gatewayInstance) return;

			if (options.gateway === 'mw') {
				// Poll the page until the payframe script is loaded then initialise.
				interval = setInterval(() => {
					if (typeof payframe !== 'undefined') {
						clearInterval(interval);

						this.initMerchantWarrior();
					}
				}, 100);
			} else if (options.gateway === 'stripe') {
				// Poll the page until the Stripe script is loaded then initialise.
				interval = setInterval(() => {
					if (typeof Stripe !== 'undefined') {
						clearInterval(interval);

						this.getStripePaymentIntent();
					}
				}, 100);
			}
		},

		handleStep0Submit() {
			const validator = new Pristine(this.$refs.step0form, pristineConfig);
			const valid = validator.validate();

			if (valid) {
				this.nextStep();
				if (isAppeal) {
					this.$dispatch('appealformfocus');
				}
			} else {
				this.$store.aa.trackFormErrors(formTrackingDetails, validator, {
					form_name: this.isMembership ? 'member signup' : 'donate',
					form_variant: 'standard',
					error_message: 'Step 1 - Client side validation failed',
				});

				if (!isAppeal) {
					this.scrollToFirstErrorMessage(validator);
				}
			}
		},
		handleStep1Submit(validateOnly = false) {
			const validator = new Pristine(this.$refs.step1form, pristineConfig);
			const valid = validator.validate();

			// When an address is selected from the Google Places autocomplete field, we need
			// to perform validation only, without submitting the form to get rid of the error messages.
			if (validateOnly) return;

			// Validate that we have the address parts necessary to send to the CRM.
			if (!this.addressManualInput && !this.hasValidAddress()) {
				this.inputs.country = 'AU';

				if (this.$refs.addressAutocomplete.value) {
					validator.addError(
						this.$refs.addressAutocomplete,
						'Please select an address from the dropdown or fill the details above.'
					);
				}

				// tracking for auto address
				this.$store.aa.trackFormErrors(formTrackingDetails, validator, {
					form_name: 'donate',
					form_variant: 'standard',
					error_message: 'Step 2 - Client side validation failed',
				});
			}

			if (valid) {
				if (!gatewayInstance) {
					this.preparePaymentGateway();
				} else if (gatewayInstance && options.gateway === 'stripe') {
					this.getStripePaymentIntent();
				}

				this.nextStep();
			} else {
				this.scrollToFirstErrorMessage(validator);

				this.$store.aa.trackFormErrors(formTrackingDetails, validator, {
					form_name: this.isMembership ? 'member signup' : 'donate',
					form_variant: 'standard',
					error_message: 'Step 2 - Client side validation failed',
				});
			}
		},
		handleStep2Submit() {
			const validator = new Pristine(this.$refs.step2form, pristineConfig);
			const valid = validator.validate();

			const submitRequest = () => {
				if (this.inputs.paymentOption === 'cc') {
					if (options.gateway === 'mw') {
						gatewayInstance.submitPayframe();
					} else if (options.gateway === 'stripe') {
						this.submitStripePayment();
					}
				} else if (this.inputs.paymentOption === 'dd') {
					this.submitDebitCardPaymentRequest(this.inputs);
				} else {
					// Overwrite the gateway to let the API know this is a PayPal payment.
					this.inputs.gateway = 'paypal';

					this.initPayPal();
				}
			};

			if (valid) {
				this.$store.turnstile.render({
					element: this.$refs.turnstile,
					callback: (token, fieldName) => {
						this.inputs[fieldName] = token;

						submitRequest();
					},
					action: 'donation',
					autoRemove: true,
				});
			} else {
				this.$store.aa.trackFormErrors(formTrackingDetails, validator, {
					form_name: this.isMembership ? 'member signup' : 'donate',
					form_variant: 'standard',
					error_message: 'Step 3 - Client side validation failed',
				});
			}
		},

		previewGiftCertificate() {
			const validator = new Pristine(this.$refs.step0form, pristineConfig);
			const valid = validator.validate();

			if (valid) {
				this.$store.aa.modal.open('donation__gift-certificate-modal');
			} else {
				this.scrollToFirstErrorMessage(validator);
			}
		},

		getGiftCertficatePreviewURL() {
			if (this.$store.aa.modal.isModalOpen) {
				return [
					'/gift-certificate/preview.jpg?',
					'&recipient_name=',
					this.inputs.giftRecipientName,
					'&donation_occasion=',
					this.inputs.giftOccasion,
					'&amount=',
					this.getDonationAmount(),
					'&message=',
					encodeURIComponent(this.inputs.giftMessage),
				].join('');
			}
			return '';
		},

		setGiftCertificatePDFURL(url) {
			if (typeof url !== 'string' && !!url) return;

			this.pdfDownloadUrl = url;
		},

		getSubmitType() {
			if (this.inputs.isGift) return 'gift certificate';

			if (updateType === 'md') return 'monthly donation upgrade';

			if (updateType === 'cc') return 'update credit card';

			if (isMembershipRenewal) return 'membership renewal';

			if (isMembershipNew) return 'new membership';

			if (isAppeal) return 'appeal';

			return 'general';
		},

		trackEvent(responseData = {}) {
			this.$store.aa.track({
				event: 'form_submit',
				form_name: this.isMembership ? 'member signup' : 'donate',
				form_variant: 'standard',
				submit_type: this.getSubmitType(),
				donation_value:
					updateType === 'md'
						? this.getIncrementedDonationAmount(this.inputs.donationAmount)
						: this.getDonationAmount(),
				donation_frequency: this.inputs.donationFrequency,
				donation_handle:
					updateType === 'md'
						? this.getAddAmount()
						: this.inputs.donationAmount,
				time_on_form: dayjs().diff(formTrackingDetails.interactionStartTime),
				data: {
					...responseData,
					isDefault,
					isUpdateCC: updateType === 'cc',
					isMembershipRenewal,
					isUpdateMD: updateType === 'md',
				},
			});

			formTrackingDetails.isNotSubmit = false;
		},

		handleOccasionChange() {
			const occastionId = parseInt(this.inputs.giftOccasion, 10);
			const occasion = occasions.find((obj) => obj.id === occastionId);

			this.isMemoryOf = occasion && occasion?.slug === 'in-memory-of';
		},
	};
});
